<template>
  <div class="addr" :class="{ 'addr--outside': isOutside }">
    <button
      type="button"
      class="addr__locate"
      :disabled="locating"
      @click="useCurrentLocation"
    >
      <span class="addr__locate-icon" aria-hidden="true">
        <i :class="locating ? 'fa-solid fa-spinner fa-spin' : 'fa-solid fa-location-crosshairs'"></i>
      </span>
      <span class="addr__locate-text">
        <strong>{{ locating ? 'Buscando tu ubicación…' : 'Usar mi ubicación actual' }}</strong>
        <small>Rellenamos la dirección por ti</small>
      </span>
    </button>
    <p v-if="locateError" class="addr__hint addr__hint--error" role="alert">{{ locateError }}</p>

    <div class="addr__divider" aria-hidden="true"><span>o busca tu calle</span></div>

    <div class="addr__search">
      <label :for="`${uid}-search`" class="addr__label">Calle y número <span class="addr__req" aria-hidden="true">*</span></label>
      <div class="addr__search-box">
        <i class="fa-solid fa-magnifying-glass" aria-hidden="true"></i>
        <input
          :id="`${uid}-search`"
          ref="searchInput"
          v-model="query"
          type="text"
          class="addr__input addr__input--search"
          placeholder="Ej.: Calle Feria 20"
          autocomplete="street-address"
          role="combobox"
          aria-autocomplete="list"
          :aria-expanded="showResults ? 'true' : 'false'"
          :aria-controls="`${uid}-results`"
          :aria-activedescendant="activeIndex >= 0 ? `${uid}-opt-${activeIndex}` : undefined"
          :aria-invalid="fieldErrors.street ? 'true' : 'false'"
          :aria-describedby="fieldErrors.street ? `${uid}-street-error` : undefined"
          aria-required="true"
          @input="onQueryInput"
          @keydown.down.prevent="moveActive(1)"
          @keydown.up.prevent="moveActive(-1)"
          @keydown.enter="onEnter"
          @keydown.esc="onEsc"
          @blur="onSearchBlur"
          @focus="showResults = results.length > 0"
        />
        <button v-if="query" type="button" class="addr__clear" aria-label="Borrar la búsqueda" @mousedown.prevent @click="clearSearch">
          <i class="fa-solid fa-xmark" aria-hidden="true"></i>
        </button>
      </div>
      <ul
        v-show="showResults"
        :id="`${uid}-results`"
        class="addr__results"
        role="listbox"
        aria-label="Direcciones encontradas"
      >
        <li
          v-for="(result, index) in results"
          :id="`${uid}-opt-${index}`"
          :key="result.id"
          role="option"
          class="addr__result"
          :class="{ 'is-active': index === activeIndex }"
          :aria-selected="index === activeIndex ? 'true' : 'false'"
          @mousedown.prevent="selectResult(result)"
        >
          <i class="fa-solid fa-location-dot" aria-hidden="true"></i>
          <span>
            <strong>{{ result.street }}<template v-if="result.number"> {{ result.number }}</template></strong>
            <small>{{ [result.postcode, result.city].filter(Boolean).join(' · ') }}</small>
          </span>
        </li>
      </ul>
      <p v-if="searching" class="addr__hint" aria-live="polite">Buscando…</p>
      <p v-else-if="noResults" class="addr__hint" aria-live="polite">No encontramos esa dirección. Prueba con otra o marca el punto en el mapa.</p>
      <p v-if="fieldErrors.street" :id="`${uid}-street-error`" class="addr__hint addr__hint--error">{{ fieldErrors.street }}</p>
    </div>

    <div class="addr__map-wrap">
      <div ref="map" class="addr__map" role="application" aria-label="Mapa: toca para marcar dónde entregamos"></div>
      <p class="addr__map-caption">
        <i class="fa-regular fa-hand-pointer" aria-hidden="true"></i>
        <span>
          Toca el mapa o arrastra el pin para ajustar el punto de entrega.
          <template v-if="maxKm"><br /><span class="addr__zone-dot" aria-hidden="true"></span> Zona de reparto: hasta {{ formatKm(maxKm) }} del restaurante.</template>
        </span>
      </p>
    </div>

    <p v-if="distanceKm !== null" class="addr__distance" :class="{ 'is-outside': isOutside }" aria-live="polite">
      <i :class="isOutside ? 'fa-solid fa-triangle-exclamation' : 'fa-solid fa-route'" aria-hidden="true"></i>
      <span v-if="isOutside">Esta dirección está a {{ formatKm(distanceKm) }} y el restaurante reparte hasta {{ formatKm(maxKm) }}.</span>
      <span v-else>A {{ formatKm(distanceKm) }} del restaurante<template v-if="maxKm"> · dentro de la zona de reparto</template></span>
    </p>

    <div class="addr__grid">
      <div class="addr__field">
        <label :for="`${uid}-number`" class="addr__label">Número <span class="addr__req" aria-hidden="true">*</span></label>
        <input
          :id="`${uid}-number`"
          ref="numberInput"
          v-model.trim="local.number"
          type="text"
          class="addr__input"
          maxlength="12"
          autocomplete="off"
          placeholder="20 o s/n"
          aria-required="true"
          :aria-invalid="fieldErrors.number ? 'true' : 'false'"
          :aria-describedby="fieldErrors.number ? `${uid}-number-error` : undefined"
          @input="emitValue"
        />
        <p v-if="fieldErrors.number" :id="`${uid}-number-error`" class="addr__hint addr__hint--error">{{ fieldErrors.number }}</p>
      </div>
      <div class="addr__field">
        <label :for="`${uid}-piso`" class="addr__label">Piso / puerta</label>
        <input
          :id="`${uid}-piso`"
          v-model.trim="local.piso"
          type="text"
          class="addr__input"
          maxlength="40"
          autocomplete="address-line2"
          placeholder="3º B"
          @input="emitValue"
        />
      </div>
      <div class="addr__field">
        <label :for="`${uid}-cp`" class="addr__label">Código postal <span class="addr__req" aria-hidden="true">*</span></label>
        <input
          :id="`${uid}-cp`"
          v-model.trim="local.codPostal"
          type="text"
          inputmode="numeric"
          class="addr__input"
          maxlength="10"
          autocomplete="postal-code"
          placeholder="41003"
          aria-required="true"
          :aria-invalid="fieldErrors.codPostal ? 'true' : 'false'"
          :aria-describedby="fieldErrors.codPostal ? `${uid}-cp-error` : undefined"
          @input="emitValue"
        />
        <p v-if="fieldErrors.codPostal" :id="`${uid}-cp-error`" class="addr__hint addr__hint--error">{{ fieldErrors.codPostal }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const MAPBOX_ACCESS_TOKEN = process.env.VUE_APP_MAPBOX_ACCESS_TOKEN || '';
const CARTO_BASEMAP_KEY = process.env.VUE_APP_CARTO_BASEMAP_KEY || '';
const SPAIN_CENTER = [40.4165, -3.70256];
let instances = 0;

function toNumber(value) {
  const number = Number(String(value ?? '').replace(',', '.'));
  return Number.isFinite(number) && value !== null && value !== '' ? number : null;
}

function haversineKm(a, b) {
  const rad = (deg) => (deg * Math.PI) / 180;
  const dLat = rad(b[0] - a[0]);
  const dLng = rad(b[1] - a[1]);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a[0])) * Math.cos(rad(b[0])) * Math.sin(dLng / 2) ** 2;
  return 6371 * 2 * Math.asin(Math.sqrt(h));
}

// "Calle Feria 20, Sevilla" -> { street: "Calle Feria", number: "20", city: "Sevilla" }
export function splitAddress(address) {
  const [first = '', ...rest] = String(address || '').split(',').map((part) => part.trim());
  const match = first.match(/^(.*?)[\s,]+(\d+[\s-]?[A-Za-z]?|s\/n)$/i);
  return {
    street: match ? match[1] : first,
    number: match ? match[2] : '',
    city: rest.filter((part) => !/^\d{4,5}$/.test(part)).join(', '),
  };
}

function parseFeature(feature) {
  const context = Array.isArray(feature?.context) ? feature.context : [];
  const find = (type) => context.find((item) => String(item.id || '').startsWith(type))?.text || '';
  const isAddress = (feature.place_type || []).includes('address');
  return {
    id: feature.id,
    street: isAddress ? feature.text : (feature.text || feature.place_name || ''),
    number: isAddress ? String(feature.address || '') : '',
    postcode: find('postcode'),
    city: find('place') || find('locality'),
    latitude: feature.center?.[1],
    longitude: feature.center?.[0],
  };
}

export default {
  name: 'DeliveryAddressPicker',
  props: {
    modelValue: { type: Object, default: () => ({}) },
    restaurant: { type: Object, default: () => ({}) },
  },
  emits: ['update:modelValue'],
  data() {
    instances += 1;
    return {
      uid: `addr-${instances}`,
      local: {
        street: '', number: '', piso: '', codPostal: '', city: '', latitude: null, longitude: null,
      },
      query: '',
      results: [],
      showResults: false,
      activeIndex: -1,
      searching: false,
      noResults: false,
      searchTimer: null,
      searchSeq: 0,
      locating: false,
      locateError: '',
      fieldErrors: {},
      map: null,
      pin: null,
    };
  },
  computed: {
    restaurantPoint() {
      const lat = toNumber(this.restaurant?.latitude);
      const lng = toNumber(this.restaurant?.longitude);
      return lat !== null && lng !== null ? [lat, lng] : null;
    },
    maxKm() {
      const km = toNumber(this.restaurant?.max_km_delivery);
      return km && km > 0 ? km : null;
    },
    point() {
      const lat = toNumber(this.local.latitude);
      const lng = toNumber(this.local.longitude);
      return lat !== null && lng !== null ? [lat, lng] : null;
    },
    distanceKm() {
      return this.point && this.restaurantPoint ? haversineKm(this.restaurantPoint, this.point) : null;
    },
    isOutside() {
      return Boolean(this.maxKm && this.distanceKm !== null && this.distanceKm > this.maxKm);
    },
  },
  created() {
    const value = this.modelValue || {};
    const split = splitAddress(value.address);
    this.local = {
      street: value.street || split.street,
      number: value.number || split.number,
      piso: value.piso || '',
      codPostal: value.codPostal || '',
      city: value.city || split.city,
      latitude: value.latitude ?? null,
      longitude: value.longitude ?? null,
    };
    this.query = this.composeStreetLine();
  },
  mounted() {
    this.initMap();
  },
  beforeUnmount() {
    clearTimeout(this.searchTimer);
    if (this.map) this.map.remove();
  },
  watch: {
    restaurant() {
      this.drawRestaurant();
      if (!this.point) this.fitRestaurant();
    },
  },
  methods: {
    formatKm(km) {
      return `${Number(km).toLocaleString('es-ES', { maximumFractionDigits: 1 })} km`;
    },
    composeStreetLine() {
      return [this.local.street, this.local.number].filter(Boolean).join(' ').trim();
    },
    composeAddress() {
      const line = this.composeStreetLine();
      return [line, this.local.city].filter(Boolean).join(', ').slice(0, 100);
    },
    emitValue() {
      this.fieldErrors = {};
      this.$emit('update:modelValue', {
        ...this.local,
        address: this.composeAddress(),
        outside: this.isOutside,
      });
    },
    // ---------- mapa
    initMap() {
      this.map = L.map(this.$refs.map, {
        zoomControl: true,
        zoomSnap: 0.25,
        scrollWheelZoom: false,
        attributionControl: false,
      });
      const tiles = 'https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png'
        + (CARTO_BASEMAP_KEY ? `?key=${encodeURIComponent(CARTO_BASEMAP_KEY)}` : '');
      L.tileLayer(tiles, { maxZoom: 20, minZoom: 3 }).addTo(this.map);
      this.drawRestaurant();
      if (this.point) {
        this.placePin(this.point, { pan: true });
      } else {
        this.fitRestaurant();
      }
      this.map.on('click', (event) => this.pickPoint([event.latlng.lat, event.latlng.lng]));
      // El mapa vive dentro de un modal: recalcular tamaño cuando ya es visible.
      setTimeout(() => {
        if (!this.map) return;
        this.map.invalidateSize();
        if (this.point) this.map.setView(this.point, Math.max(this.map.getZoom() || 0, 17));
        else this.fitRestaurant();
      }, 250);
    },
    drawRestaurant() {
      if (!this.map) return;
      if (this.restaurantLayer) this.restaurantLayer.remove();
      if (!this.restaurantPoint) return;
      const layers = [];
      if (this.maxKm) {
        layers.push(L.circle(this.restaurantPoint, {
          radius: this.maxKm * 1000,
          color: '#16a34a',
          weight: 2,
          fillColor: '#22c55e',
          fillOpacity: 0.08,
          interactive: false,
        }));
      }
      layers.push(L.marker(this.restaurantPoint, {
        keyboard: false,
        interactive: false,
        icon: L.divIcon({
          className: 'addr-restaurant-icon',
          html: '<span><i class="fa-solid fa-utensils"></i></span>',
          iconSize: [34, 34],
          iconAnchor: [17, 17],
        }),
      }));
      this.restaurantLayer = L.layerGroup(layers).addTo(this.map);
    },
    fitRestaurant() {
      if (!this.map) return;
      if (this.restaurantPoint && this.maxKm) {
        this.map.fitBounds(L.latLng(this.restaurantPoint).toBounds(this.maxKm * 2000), { padding: [10, 10] });
      } else if (this.restaurantPoint) {
        this.map.setView(this.restaurantPoint, 14);
      } else {
        this.map.setView(SPAIN_CENTER, 5);
      }
    },
    placePin(point, { pan = false } = {}) {
      if (!this.map) return;
      if (!this.pin) {
        this.pin = L.marker(point, {
          draggable: true,
          autoPan: true,
          title: 'Punto de entrega',
          alt: 'Punto de entrega',
          icon: L.divIcon({
            className: 'addr-pin-icon',
            html: '<span><i class="fa-solid fa-house"></i></span>',
            iconSize: [36, 44],
            iconAnchor: [18, 42],
          }),
        }).addTo(this.map);
        this.pin.on('dragend', () => {
          const { lat, lng } = this.pin.getLatLng();
          this.pickPoint([lat, lng], { keepPin: true });
        });
      } else {
        this.pin.setLatLng(point);
      }
      if (pan) this.map.setView(point, Math.max(this.map.getZoom() || 0, 17));
    },
    async pickPoint(point, { keepPin = false } = {}) {
      this.local.latitude = point[0];
      this.local.longitude = point[1];
      if (!keepPin) this.placePin(point);
      this.emitValue();
      const found = await this.reverseGeocode(point);
      if (found) this.applyResult(found, { keepPoint: true });
    },
    // ---------- geocodificación (Mapbox)
    async reverseGeocode(point) {
      if (!MAPBOX_ACCESS_TOKEN) return null;
      try {
        const { data } = await axios.get(
          `https://api.mapbox.com/geocoding/v5/mapbox.places/${point[1]},${point[0]}.json`,
          { params: { access_token: MAPBOX_ACCESS_TOKEN, types: 'address', language: 'es', limit: 1 } },
        );
        const feature = data?.features?.[0];
        return feature ? parseFeature(feature) : null;
      } catch (error) {
        return null;
      }
    },
    onQueryInput() {
      this.local.street = this.query;
      this.local.number = '';
      this.emitValue();
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(this.search, 250);
    },
    async search() {
      const query = this.query.trim();
      this.noResults = false;
      if (query.length < 3 || !MAPBOX_ACCESS_TOKEN) {
        this.results = [];
        this.showResults = false;
        return;
      }
      const seq = ++this.searchSeq;
      this.searching = true;
      try {
        const params = {
          access_token: MAPBOX_ACCESS_TOKEN,
          country: 'es',
          language: 'es',
          types: 'address,poi',
          autocomplete: true,
          limit: 6,
        };
        if (this.restaurantPoint) params.proximity = `${this.restaurantPoint[1]},${this.restaurantPoint[0]}`;
        const { data } = await axios.get(
          `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json`,
          { params },
        );
        if (seq !== this.searchSeq) return;
        this.results = (data?.features || []).map(parseFeature).filter((item) => item.street);
        this.noResults = this.results.length === 0;
        this.showResults = this.results.length > 0;
        this.activeIndex = -1;
      } catch (error) {
        if (seq === this.searchSeq) this.results = [];
      } finally {
        if (seq === this.searchSeq) this.searching = false;
      }
    },
    moveActive(step) {
      if (!this.results.length) return;
      this.showResults = true;
      this.activeIndex = (this.activeIndex + step + this.results.length) % this.results.length;
    },
    onEnter(event) {
      if (this.showResults && this.activeIndex >= 0) {
        event.preventDefault();
        this.selectResult(this.results[this.activeIndex]);
      }
    },
    onEsc(event) {
      // Esc cierra primero la lista; solo si no hay lista llega al modal.
      if (this.showResults) {
        event.stopPropagation();
        this.closeResults();
      }
    },
    closeResults() {
      this.showResults = false;
      this.activeIndex = -1;
    },
    onSearchBlur() {
      setTimeout(this.closeResults, 120);
    },
    clearSearch() {
      this.query = '';
      this.local.street = '';
      this.local.number = '';
      this.local.latitude = null;
      this.local.longitude = null;
      if (this.pin) {
        this.pin.remove();
        this.pin = null;
      }
      this.results = [];
      this.closeResults();
      this.emitValue();
      this.$refs.searchInput?.focus();
    },
    selectResult(result) {
      this.applyResult(result);
      this.closeResults();
      // Si la calle no traía número, llevamos al cliente a escribirlo.
      this.$nextTick(() => {
        if (!this.local.number) this.$refs.numberInput?.focus();
      });
    },
    applyResult(result, { keepPoint = false } = {}) {
      this.local.street = result.street || this.local.street;
      if (result.number || !keepPoint) this.local.number = result.number || this.local.number;
      if (result.postcode) this.local.codPostal = result.postcode;
      if (result.city) this.local.city = result.city;
      if (!keepPoint && result.latitude != null && result.longitude != null) {
        this.local.latitude = result.latitude;
        this.local.longitude = result.longitude;
        this.placePin([result.latitude, result.longitude], { pan: true });
      }
      this.query = this.composeStreetLine();
      this.emitValue();
    },
    useCurrentLocation() {
      this.locateError = '';
      if (!navigator.geolocation) {
        this.locateError = 'Tu navegador no permite compartir la ubicación. Escribe la dirección.';
        return;
      }
      this.locating = true;
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const point = [position.coords.latitude, position.coords.longitude];
          this.local.latitude = point[0];
          this.local.longitude = point[1];
          this.placePin(point, { pan: true });
          const found = await this.reverseGeocode(point);
          if (found) {
            this.applyResult(found, { keepPoint: true });
          } else {
            this.emitValue();
            this.locateError = 'Hemos marcado tu ubicación, pero no encontramos la calle. Escríbela, por favor.';
          }
          this.locating = false;
          this.$nextTick(() => {
            if (!this.local.number) this.$refs.numberInput?.focus();
          });
        },
        (error) => {
          this.locating = false;
          this.locateError = error?.code === 1
            ? 'No nos has dado permiso para ver tu ubicación. Puedes escribir la dirección.'
            : 'No hemos podido obtener tu ubicación. Escribe la dirección.';
        },
        { enableHighAccuracy: true, timeout: 12000, maximumAge: 60000 },
      );
    },
    // Lo llama el formulario antes de enviar. Devuelve el primer error o ''.
    validate() {
      const errors = {};
      if (!this.local.street || !this.point) {
        errors.street = 'Busca tu calle y elige una de la lista, o usa tu ubicación.';
      }
      if (!this.local.number) errors.number = 'Indica el número (o «s/n»).';
      if (!/^\d{5}$/.test(this.local.codPostal || '')) errors.codPostal = 'El código postal tiene 5 cifras.';
      this.fieldErrors = errors;
      const firstKey = Object.keys(errors)[0];
      if (firstKey) {
        const refName = { street: 'searchInput', number: 'numberInput' }[firstKey];
        this.$nextTick(() => {
          if (refName) this.$refs[refName]?.focus();
          else document.getElementById(`${this.uid}-cp`)?.focus();
        });
        return errors[firstKey];
      }
      if (this.isOutside) {
        return `Esta dirección queda fuera de la zona de reparto (hasta ${this.formatKm(this.maxKm)}).`;
      }
      return '';
    },
  },
};
</script>

<style scoped>
.addr { display: grid; gap: 12px; }

.addr__locate {
  display: flex; align-items: center; gap: 12px; width: 100%;
  padding: 12px 14px; border: 1.5px solid color-mix(in srgb, var(--primary-color, #f47700) 45%, #fff);
  border-radius: 14px; background: color-mix(in srgb, var(--primary-color, #f47700) 8%, #fff);
  color: #111827; text-align: left; transition: background-color .15s, border-color .15s;
}
.addr__locate:hover:not(:disabled) { background: color-mix(in srgb, var(--primary-color, #f47700) 14%, #fff); }
.addr__locate:focus-visible { outline: 3px solid color-mix(in srgb, var(--primary-color, #f47700) 55%, #fff); outline-offset: 2px; }
.addr__locate:disabled { opacity: .7; }
.addr__locate-icon {
  display: inline-flex; align-items: center; justify-content: center; flex: 0 0 40px; height: 40px;
  border-radius: 50%; background: var(--primary-color, #f47700); color: #fff; font-size: 1.05rem;
}
.addr__locate-text { display: flex; flex-direction: column; line-height: 1.25; }
.addr__locate-text strong { font-size: .98rem; }
.addr__locate-text small { color: #6b7280; font-size: .8rem; }

.addr__divider { display: flex; align-items: center; gap: 10px; color: #9ca3af; font-size: .78rem; text-transform: uppercase; letter-spacing: .05em; }
.addr__divider::before, .addr__divider::after { content: ''; flex: 1; height: 1px; background: #e5e7eb; }

.addr__label { display: block; margin-bottom: 6px; font-size: .86rem; font-weight: 600; color: #374151; }
.addr__req { color: #dc2626; }
.addr__input {
  width: 100%; min-height: 46px; padding: 10px 12px; border: 1.5px solid #d1d5db; border-radius: 12px;
  background: #fff; color: #111827; font-size: 1rem; transition: border-color .15s, box-shadow .15s;
}
.addr__input:focus { outline: none; border-color: var(--primary-color, #f47700); box-shadow: 0 0 0 3px color-mix(in srgb, var(--primary-color, #f47700) 22%, transparent); }
.addr__input[aria-invalid='true'] { border-color: #dc2626; }

.addr__search { position: relative; }
.addr__search-box { position: relative; }
.addr__search-box > .fa-magnifying-glass { position: absolute; left: 14px; top: 50%; transform: translateY(-50%); color: #9ca3af; pointer-events: none; }
.addr__input--search { padding-left: 40px; padding-right: 42px; }
.addr__clear {
  position: absolute; right: 6px; top: 50%; transform: translateY(-50%); width: 34px; height: 34px;
  border: 0; border-radius: 50%; background: transparent; color: #6b7280;
}
.addr__clear:hover { background: #f3f4f6; }

.addr__results {
  position: absolute; z-index: 30; left: 0; right: 0; top: calc(100% + 4px); margin: 0; padding: 6px;
  list-style: none; max-height: 260px; overflow-y: auto; border: 1px solid #e5e7eb; border-radius: 14px;
  background: #fff; box-shadow: 0 16px 32px rgba(15, 23, 42, .14);
}
.addr__result { display: flex; align-items: flex-start; gap: 10px; padding: 10px; border-radius: 10px; cursor: pointer; }
.addr__result i { margin-top: 3px; color: var(--primary-color, #f47700); }
.addr__result span { display: flex; flex-direction: column; min-width: 0; }
.addr__result strong { font-size: .95rem; color: #111827; font-weight: 600; }
.addr__result small { color: #6b7280; font-size: .8rem; }
.addr__result.is-active, .addr__result:hover { background: #f3f4f6; }

.addr__map-wrap { border-radius: 14px; overflow: hidden; border: 1px solid #e5e7eb; }
.addr__map { height: 220px; z-index: 0; }
.addr__map-caption { display: flex; align-items: center; gap: 8px; margin: 0; padding: 8px 12px; background: #f9fafb; color: #6b7280; font-size: .8rem; }

.addr__zone-dot { display: inline-block; width: 10px; height: 10px; margin-right: 4px; border-radius: 50%; border: 2px solid #16a34a; background: rgba(34, 197, 94, .18); vertical-align: -1px; }
.addr__distance { display: flex; align-items: center; gap: 8px; margin: 0; padding: 9px 12px; border-radius: 12px; background: #f0fdf4; color: #166534; font-size: .86rem; font-weight: 600; }
.addr__distance.is-outside { background: #fef2f2; color: #b91c1c; }

.addr__grid { display: grid; grid-template-columns: 1fr 1fr 1.2fr; gap: 10px; }
.addr__field { min-width: 0; }

.addr__hint { margin: 6px 2px 0; font-size: .8rem; color: #6b7280; }
.addr__hint--error { color: #b91c1c; font-weight: 600; }

@media (max-width: 420px) {
  .addr__grid { grid-template-columns: 1fr 1fr; }
  .addr__grid .addr__field:last-child { grid-column: 1 / -1; }
}
</style>

<style>
.addr-restaurant-icon span {
  display: flex; align-items: center; justify-content: center; width: 34px; height: 34px; border-radius: 50%;
  background: #111827; color: #fff; font-size: 14px; border: 3px solid #fff; box-shadow: 0 4px 10px rgba(0,0,0,.25);
}
.addr-pin-icon span {
  display: flex; align-items: center; justify-content: center; width: 36px; height: 36px; border-radius: 50% 50% 50% 0;
  transform: rotate(-45deg); background: var(--primary-color, #f47700); border: 3px solid #fff; box-shadow: 0 6px 14px rgba(0,0,0,.28);
}
.addr-pin-icon span i { transform: rotate(45deg); color: #fff; font-size: 14px; }
</style>
